import { EnterpriseDetails } from 'APIs/enterprises/enterprises.types';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import { FormItem, Label } from 'Constants/styles';
import { MouseEvent, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import * as Yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';

import { FormCheckbox, FormText, FormWrapper, VInput } from './styles';
import { CreateMarketplaceFormProps } from './types';

type Props = {
  saveMarketplaceHandler: (formData: CreateMarketplaceFormProps) => void;
  modalErrorMsg: string;
  isModalLoading: boolean;
  setShowModal: (showModal: boolean) => void;
  fetchEnterprises: (filterText?: string) => Promise<Array<EnterpriseDetails>>;
  successData: { id: string; apiKey: string } | null;
};

const CreateMarketplaceModal = ({
  saveMarketplaceHandler,
  modalErrorMsg,
  isModalLoading,
  setShowModal,
  fetchEnterprises,
  successData,
}: Props): ReactElement => {
  const [hideError, setHideError] = useState<boolean>(false);
  const errorContainerRef = useRef<HTMLDivElement>(null);

  const validationSchema = Yup.object().shape({
    enterpriseId: Yup.object({
      value: Yup.string().required('Enterprise is required'),
      label: Yup.string().required(),
    }).required('Enterprise is required'),
    slug: Yup.string()
      .trim()
      .matches(/^[a-z0-9]+$/, 'Must contain only lowercase letters and digits')
      .required('Slug is required'),
    businessName: Yup.string().trim().required('Business name is required'),
    website: Yup.string().trim().url('Must be a valid URL').required('Website is required'),
    companyEmail: Yup.string().trim().email('Must be a valid email').required('Company email is required'),
    companyPhone: Yup.string().trim().required('Company phone is required'),
    zendeskUserGroupId: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value)),
    enableMerchantDashboardAccess: Yup.boolean().required('Enable Merchant Dashboard Access is required'),
    shortenerBaseUri: Yup.string().url('Must be a valid URL').optional().nullable(),
    enableMarketplaceCustomerUsers: Yup.boolean().optional(),
  });

  const defaultValues: CreateMarketplaceFormProps = {
    slug: '',
    businessName: '',
    website: '',
    companyEmail: '',
    companyPhone: '',
    enableMerchantDashboardAccess: false,
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CreateMarketplaceFormProps>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultValues,
  });

  const onSubmit = useCallback(
    async (formData) => {
      saveMarketplaceHandler(formData);
    },
    [saveMarketplaceHandler],
  );

  const handleFormSubmit = useCallback(() => {
    return handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit]);

  const handleCancel = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.preventDefault();
      setHideError(true);
      setShowModal(false);
    },
    [setShowModal],
  );

  useEffect(() => {
    if (modalErrorMsg) {
      setHideError(false);
      if (errorContainerRef && errorContainerRef.current) {
        errorContainerRef.current.scrollIntoView();
      }
    }
  }, [modalErrorMsg]);

  const promiseOptions = useCallback(
    async (inputValue?: string) => {
      const options = await fetchEnterprises(inputValue);
      return options.map((enterprise) => ({
        value: enterprise.EnterpriseDetails.enterpriseId,
        label: enterprise.EnterpriseDetails.businessName,
      }));
    },
    [fetchEnterprises],
  );

  return (
    <Modal
      title={'Add new marketplace'}
      cancelBtnText={successData ? 'Close' : 'Cancel'}
      confirmBtnText={'Add'}
      handleCancel={handleCancel}
      handleSubmit={handleFormSubmit()}
      hideConfirmBtn={!!successData}
      disableConfirmBtn={isModalLoading}
      isLoading={isModalLoading}
      large={false}
      testId={'create-marketplace-modal'}
    >
      <div ref={errorContainerRef}>
        {modalErrorMsg.length > 0 && !hideError && (
          <Message
            className="mt-2 mb-0"
            success={false}
            description={modalErrorMsg}
            handleClose={(e) => {
              e.preventDefault();
              setHideError(true);
            }}
          />
        )}
      </div>
      {successData && (
        <>
          <FormText>Marketplace created successfully!</FormText>
          <FormText>
            IMPORTANT: API key will be displayed only once. Ensure you copy and securely store it, as it cannot be
            retrieved later.
          </FormText>
          <FormItem>
            <Label>API Key</Label>
            <VInput value={successData.apiKey} readOnly={true} />
          </FormItem>
          <FormItem>
            <Label>Marketplace ID</Label>
            <VInput value={successData.id} readOnly={true} />
          </FormItem>
        </>
      )}
      {!successData && (
        <FormWrapper>
          <FormItem>
            <Label>Enterprise*</Label>
            <Controller
              name="enterpriseId"
              control={control}
              render={({ field }) => (
                <AsyncSelect
                  {...field}
                  cacheOptions
                  defaultOptions
                  loadOptions={promiseOptions}
                  className={errors.enterpriseId ? 'react-select-container mb-2 error' : 'react-select-container mb-2'}
                  placeholder="Type enterprise readable identifier..."
                  onChange={(selectedOption: { value: string; label: string }) => {
                    return field.onChange(selectedOption);
                  }}
                />
              )}
            />
            {errors.enterpriseId?.value && <span className="error-msg mt-10">{errors.enterpriseId.value.message}</span>}
          </FormItem>
          <FormItem>
            <Label>Slug*</Label>
            <VInput {...register('slug')} isValid={!errors.slug} />
            {errors.slug && <span className="error-msg">{errors.slug.message}</span>}
          </FormItem>
          <FormItem>
            <Label>Business name*</Label>
            <VInput {...register('businessName')} isValid={!errors.businessName} />
            {errors.businessName && <span className="error-msg">{errors.businessName.message}</span>}
          </FormItem>
          <FormItem>
            <Label>Web site*</Label>
            <VInput {...register('website')} isValid={!errors.website} />
            {errors.website && <span className="error-msg">{errors.website.message}</span>}
          </FormItem>
          <FormItem>
            <Label>Email address*</Label>
            <VInput {...register('companyEmail')} isValid={!errors.companyEmail} />
            {errors.companyEmail && <span className="error-msg">{errors.companyEmail.message}</span>}
          </FormItem>
          <FormItem>
            <Label>Phone number*</Label>
            <VInput {...register('companyPhone')} isValid={!errors.companyPhone} />
            {errors.companyPhone && <span className="error-msg">{errors.companyPhone.message}</span>}
          </FormItem>
          <FormItem>
            <Label>Zendesk user group ID</Label>
            <VInput {...register('zendeskUserGroupId')} isValid={!errors.zendeskUserGroupId} />
            {errors.zendeskUserGroupId && <span className="error-msg">{errors.zendeskUserGroupId.message}</span>}
          </FormItem>
          <FormItem>
            <Label>Shortener base URI</Label>
            <VInput {...register('shortenerBaseUri')} isValid={!errors.shortenerBaseUri} />
            {errors.shortenerBaseUri && <span className="error-msg">{errors.shortenerBaseUri.message}</span>}
          </FormItem>
          <FormItem>
            <Label>Enable merchant dashboard access</Label>
            <FormCheckbox type="checkbox" {...register('enableMerchantDashboardAccess')} />
            {errors.enableMerchantDashboardAccess && (
              <span className="error-msg">{errors.enableMerchantDashboardAccess.message}</span>
            )}
          </FormItem>
          <FormItem>
            <Label>Enable marketplace customer users</Label>
            <FormCheckbox type="checkbox" {...register('enableMarketplaceCustomerUsers')} />
            {errors.enableMarketplaceCustomerUsers && (
              <span className="error-msg">{errors.enableMarketplaceCustomerUsers.message}</span>
            )}
          </FormItem>
        </FormWrapper>
      )}
    </Modal>
  );
};

export default CreateMarketplaceModal;
